// src/services/peopleAttention.ts
//
// Deterministic "People Requiring Attention" ranking. Composes existing
// contact-context signals + opportunities + referrals. No AI, no generated text.
//
// Tiers (lower = more urgent):
//   0 overdue task tied to contact
//   1 meeting within 48h
//   2 opportunity closing soon
//   3 stale interaction
//   4 referral awaiting follow-up

import type { Opportunity, Referral } from '../types/api';

export type PersonTier = 0 | 1 | 2 | 3 | 4;

export interface PersonAttentionItem {
  /** Contact id when known (referral without a contact has null). */
  contactId: string | null;
  name: string;
  tier: PersonTier;
  reason: string;
  actionPath: string;
}

/** Minimal projection of a contact's context signals (from useHomeContactInsights). */
export interface PersonContactInsight {
  contactId: string;
  name: string;
  signals: {
    urgentTasks: Array<{ dueDate?: string | null }>;
    upcomingEvents: Array<{ startAt?: string | null }>;
    staleInteraction: boolean;
  };
}

export interface DerivePeopleAttentionParams {
  insights: PersonContactInsight[];
  opportunities: Opportunity[];
  referrals: Referral[];
  today: string; // YYYY-MM-DD
  meetingWithinDays?: number;
  opportunityCloseWithinDays?: number;
  max?: number;
}

const dateOnly = (s?: string | null): string | null => (s ? s.split('T')[0] : null);
function addDays(today: string, days: number): string {
  const d = new Date(`${today}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}
const contactLink = (id: string) => `/contacts?contactId=${id}`;

export function derivePeopleAttention({
  insights,
  opportunities,
  referrals,
  today,
  meetingWithinDays = 2,
  opportunityCloseWithinDays = 14,
  max = 5,
}: DerivePeopleAttentionParams): PersonAttentionItem[] {
  const candidates: PersonAttentionItem[] = [];

  // From contact context: tiers 0 (overdue task), 1 (meeting ≤48h), 3 (stale).
  for (const c of insights) {
    const overdue = c.signals.urgentTasks.some((t) => {
      const d = dateOnly(t.dueDate);
      return !!d && d < today;
    });
    const meetingSoon = c.signals.upcomingEvents.some((e) => {
      const d = dateOnly(e.startAt);
      return !!d && d >= today && d <= addDays(today, meetingWithinDays);
    });

    if (overdue) {
      candidates.push({ contactId: c.contactId, name: c.name, tier: 0, reason: 'Overdue task tied to this contact', actionPath: contactLink(c.contactId) });
    } else if (meetingSoon) {
      candidates.push({ contactId: c.contactId, name: c.name, tier: 1, reason: 'Meeting within 48 hours', actionPath: contactLink(c.contactId) });
    } else if (c.signals.staleInteraction) {
      candidates.push({ contactId: c.contactId, name: c.name, tier: 3, reason: 'No recent interaction', actionPath: contactLink(c.contactId) });
    }
  }

  // Tier 2: opportunity closing soon (open, expectedCloseDate ≤ today + window; includes overdue close).
  const closeBy = addDays(today, opportunityCloseWithinDays);
  for (const o of opportunities) {
    if (o.status !== 'open' || !o.contactId) continue;
    const d = dateOnly(o.expectedCloseDate);
    if (!d || d > closeBy) continue;
    const overdueClose = d < today;
    candidates.push({
      contactId: o.contactId,
      name: o.contact?.fullName ?? 'Contact',
      tier: 2,
      reason: overdueClose ? `Opportunity "${o.title}" close date passed` : `Opportunity "${o.title}" closing by ${new Date(d).toLocaleDateString()}`,
      actionPath: contactLink(o.contactId),
    });
  }

  // Tier 4: referral awaiting follow-up (open / in_progress).
  for (const r of referrals) {
    if (r.status !== 'open' && r.status !== 'in_progress') continue;
    const contact = r.subjectContact ?? r.recipientContact ?? null;
    candidates.push({
      contactId: contact?.id ?? null,
      name: contact?.fullName ?? r.recipientCircle?.name ?? 'Referral',
      tier: 4,
      reason: 'Referral awaiting follow-up',
      actionPath: contact ? contactLink(contact.id) : '/referrals',
    });
  }

  // Dedup by contact, keeping the most-urgent (lowest) tier. Null-contact items
  // (referrals without a contact) are kept individually.
  const byContact = new Map<string, PersonAttentionItem>();
  const looseItems: PersonAttentionItem[] = [];
  for (const item of candidates) {
    if (!item.contactId) { looseItems.push(item); continue; }
    const existing = byContact.get(item.contactId);
    if (!existing || item.tier < existing.tier) byContact.set(item.contactId, item);
  }

  return [...byContact.values(), ...looseItems]
    .sort((a, b) => a.tier - b.tier)
    .slice(0, max);
}
